<template>
  <div class="category-detail">
    <!-- 固定位置的联系按钮 -->
    <div class="contact-button" @click="scrollToContact">
      <a-button type="primary" size="large">
        联系我们
      </a-button>
    </div>

    <!-- 分类标题 -->
    <div class="category-header">
      <h1>{{ categoryTitle }}</h1>
      <p>{{ categoryDescription }}</p>
    </div>

    <!-- 加载状态 -->
    <div class="loading-container" v-if="loading">
      <a-spin size="large" />
    </div>

    <!-- 图片网格 -->
    <div v-else class="image-grid">
      <div v-for="(image, index) in images" 
           :key="index" 
           class="image-card" 
           @click="viewPhotoSet(image.id)"
      >
        <img :src="image.url" :alt="image.title" />
        <div class="image-info">
          <div class="info-text">{{ image.title }}</div>
          <div class="info-hover">{{ image.description }}</div>
        </div>
      </div>
      
      <!-- 无数据提示 -->
      <div v-if="images.length === 0" class="no-data">
        <a-empty description="暂无图片" />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'

export default defineComponent({
  name: 'CategoryDetail',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const category = route.params.category
    const loading = ref(false)
    const images = ref([])

    // 根据分类显示不同的标题和描述
    const categoryTitle = computed(() => {
      const titles = {
        'asian-male': '亚洲男士写真',
        'asian-female': '亚洲女士写真',
        'european-male': '欧洲男士写真',
        'european-female': '欧洲女士写真'
      }
      return titles[category] || '写真展示'
    })

    const categoryDescription = computed(() => {
      const descriptions = {
        'asian-male': '展现东方男性的独特魅力与气质',
        'asian-female': '捕捉东方女性的优雅与灵动',
        'european-male': '展现西方男性的阳刚与魅力',
        'european-female': '呈现西方女性的自信与风采'
      }
      return descriptions[category] || '精选写真作品展示'
    })

    // 从后端获取图片数据
    const fetchImages = async () => {
      try {
        loading.value = true
        const response = await fetch(`http://bobby88888.com/api/photos/${category}`)
        if (!response.ok) {
          throw new Error('获取数据失败')
        }
        const data = await response.json()
        images.value = data.map(item => ({
          id: item.id,
          url: item.coverUrl,
          title: item.title,
          // description: item.description
        }))
      } catch (error) {
        message.error('获取图片失败：' + error.message)
      } finally {
        loading.value = false
      }
    }

    const viewPhotoSet = (setId) => {
      router.push(`/store/${category}/set/${setId}`)
    }

    const scrollToContact = () => {
      router.push('/contact')
    }

    // 组件挂载时获取数据
    onMounted(() => {
      fetchImages()
    })

    return {
      images,
      categoryTitle,
      categoryDescription,
      viewPhotoSet,
      loading,
      scrollToContact
    }
  }
})
</script>

<style scoped>
.category-detail {
  min-height: 100vh;
  background: linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%);
  color: #333;
  padding: 40px 20px;
  position: relative;
  overflow: hidden;
}

.category-detail::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(135deg, rgba(173, 216, 230, 0.1) 0%, rgba(135, 206, 235, 0.1) 100%);
  transform: rotate(-45deg);
  z-index: 1;
}

.contact-button {
  position: fixed;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1000;
}

.contact-button :deep(.ant-btn) {
  background: rgba(24, 144, 255, 0.8);
  border: none;
  padding: 0 24px;
  height: 44px;
  font-size: 16px;
  backdrop-filter: blur(5px);
}

.contact-button :deep(.ant-btn:hover) {
  background: rgba(24, 144, 255, 1);
}

.category-header {
  text-align: center;
  padding: 40px 20px;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;
}

.category-header h1 {
  font-size: 2.5rem;
  margin-bottom: 16px;
  font-weight: 300;
  color: #1890ff;
}

.category-header p {
  font-size: 1.2rem;
  opacity: 0.8;
  max-width: 600px;
  margin: 0 auto;
  color: #666;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.image-card {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  aspect-ratio: 3/4;
  cursor: pointer;
  background: #fff;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.image-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
}

.image-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-card:hover img {
  transform: scale(1.05);
}

.image-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.95), transparent);
  color: #333;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.image-card:hover .image-info {
  transform: translateY(0);
}

.info-text {
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 8px;
}

.info-hover {
  font-size: 0.9rem;
  opacity: 0.8;
}

@media (max-width: 768px) {
  .category-detail {
    padding: 20px 10px;
  }

  .image-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 16px;
    padding: 16px;
  }

  .category-header h1 {
    font-size: 2rem;
  }

  .category-header p {
    font-size: 1rem;
  }

  .contact-button {
    right: 20px;
  }
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.no-data {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}
</style> 